import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "livestockSelect", "categorySelect", "subCategorySelect", "breedSelect",
    "livestockTypeId", "productionSystemId", "sexTypeConfigId", "ageTypeConfigId",
    "categoryTrigger", "categoryContent",
    "subCategoryTrigger", "subCategoryContent",
    "breedTrigger", "breedContent",
    "itemTemplate"
  ]
  
  static values = {
    configs: Array,
    breeds: Array
  }

  connect() {
    this.configsMap = this.buildConfigsMap()
  }

  buildConfigsMap() {
    const map = new Map()
    
    this.configsValue.forEach(config => {
      const key = config.livestock_with_system
      
      if (!map.has(key)) {
        map.set(key, {
          livestock_type_id: config.livestock_type_id,
          livestock_name: config.livestock_name,
          production_system_id: config.production_system_id,
          categories: new Map()
        })
      }
      
      const livestockEntry = map.get(key)
      const catKey = config.sex_type_name
      
      if (!livestockEntry.categories.has(catKey)) {
        livestockEntry.categories.set(catKey, {
          sex_type_id: config.sex_type_id,
          subCategories: []
        })
      }
      
      const existingSubCats = livestockEntry.categories.get(catKey).subCategories
      if (!existingSubCats.find(sc => sc.age_type_id === config.age_type_id)) {
        existingSubCats.push({
          age_type_id: config.age_type_id,
          age_type_name: config.age_type_name
        })
      }
    })
    
    return map
  }

  selectLivestock(event) {
    event.stopPropagation()
    
    const selectedValue = event.currentTarget.dataset.value
    const selectedText = event.currentTarget.innerText.trim()
    
    const valueTarget = this.livestockSelectTarget.querySelector('[data-ruby-ui--select-target="value"]')
    const inputTarget = this.livestockSelectTarget.querySelector('[data-ruby-ui--select-target="input"]')
    
    if (valueTarget) valueTarget.innerText = selectedText
    if (inputTarget) inputTarget.value = selectedValue
    
    const config = this.configsMap.get(selectedValue)
    
    if (config) {
      this.livestockTypeIdTarget.value = config.livestock_type_id
      this.productionSystemIdTarget.value = config.production_system_id || ''
      
      // Populate breeds based on livestock_name
      this.populateBreedDropdown(config.livestock_name)
      
      this.populateCategoryDropdown(config.categories)
      this.clearSubCategoryDropdown()
    } else {
      this.clearBreedDropdown()
      this.clearCategoryDropdown()
      this.clearSubCategoryDropdown()
    }
    
    this.closeDropdown(this.livestockSelectTarget)
  }

  selectBreed(event) {
    event.stopPropagation()
    
    const selectedValue = event.currentTarget.dataset.value
    const selectedText = event.currentTarget.innerText.trim()
    
    const valueTarget = this.breedTriggerTarget.querySelector('[data-ruby-ui--select-target="value"]')
    const inputTarget = this.breedSelectTarget.querySelector('[data-ruby-ui--select-target="input"]')
    
    if (valueTarget) valueTarget.innerText = selectedText
    if (inputTarget) inputTarget.value = selectedValue
    
    this.closeDropdown(this.breedSelectTarget)
  }

  selectCategory(event) {
    event.stopPropagation()
    
    const selectedValue = event.currentTarget.dataset.value
    const selectedText = event.currentTarget.innerText.trim()
    const sexTypeId = event.currentTarget.dataset.sexTypeId
    
    const valueTarget = this.categoryTriggerTarget.querySelector('[data-ruby-ui--select-target="value"]')
    if (valueTarget) valueTarget.innerText = selectedText
    
    // Update both the hidden field AND the RubyUI SelectInput for validation
    this.sexTypeConfigIdTarget.value = sexTypeId
    const categoryInput = this.categorySelectTarget.querySelector('[data-ruby-ui--select-target="input"]')
    if (categoryInput) categoryInput.value = sexTypeId
    
    const livestockInput = this.livestockSelectTarget.querySelector('[data-ruby-ui--select-target="input"]')
    const livestockValue = livestockInput?.value
    const config = this.configsMap.get(livestockValue)
    
    if (config && config.categories.has(selectedValue)) {
      const categoryConfig = config.categories.get(selectedValue)
      this.populateSubCategoryDropdown(categoryConfig.subCategories)
    } else {
      this.clearSubCategoryDropdown()
    }
    
    this.closeDropdown(this.categorySelectTarget)
  }

  selectSubCategory(event) {
    event.stopPropagation()
    
    const selectedValue = event.currentTarget.dataset.value
    const selectedText = event.currentTarget.innerText.trim()
    
    const valueTarget = this.subCategoryTriggerTarget.querySelector('[data-ruby-ui--select-target="value"]')
    if (valueTarget) valueTarget.innerText = selectedText
    
    // Update both the hidden field AND the RubyUI SelectInput for validation
    this.ageTypeConfigIdTarget.value = selectedValue
    const subCategoryInput = this.subCategorySelectTarget.querySelector('[data-ruby-ui--select-target="input"]')
    if (subCategoryInput) subCategoryInput.value = selectedValue
    
    this.closeDropdown(this.subCategorySelectTarget)
  }

  populateBreedDropdown(livestockName) {
    const groupContainer = this.breedContentTarget.querySelector('[data-select-group]')
    if (!groupContainer) return
    
    groupContainer.querySelectorAll('[data-select-item]').forEach(item => item.remove())
    
    // Filter breeds by livestock name (production_systems array contains livestock_name)
    const filteredBreeds = this.breedsValue.filter(breed => 
      breed.production_systems && breed.production_systems.includes(livestockName)
    )
    
    filteredBreeds.forEach(breed => {
      const item = this.createSelectItem(breed.name, breed.name, 'selectBreed')
      groupContainer.appendChild(item)
    })
    
    const valueTarget = this.breedTriggerTarget.querySelector('[data-ruby-ui--select-target="value"]')
    if (valueTarget) valueTarget.innerText = 'Select breed'
    
    const inputTarget = this.breedSelectTarget.querySelector('[data-ruby-ui--select-target="input"]')
    if (inputTarget) inputTarget.value = ''
  }

  populateCategoryDropdown(categories) {
    const groupContainer = this.categoryContentTarget.querySelector('[data-select-group]')
    if (!groupContainer) return
    
    groupContainer.querySelectorAll('[data-select-item]').forEach(item => item.remove())
    
    categories.forEach((value, name) => {
      const item = this.createSelectItem(name, name, 'selectCategory')
      item.dataset.sexTypeId = value.sex_type_id
      groupContainer.appendChild(item)
    })
    
    const valueTarget = this.categoryTriggerTarget.querySelector('[data-ruby-ui--select-target="value"]')
    if (valueTarget) valueTarget.innerText = 'Select category'
    this.sexTypeConfigIdTarget.value = ''
    
    const categoryInput = this.categorySelectTarget.querySelector('[data-ruby-ui--select-target="input"]')
    if (categoryInput) categoryInput.value = ''
  }

  populateSubCategoryDropdown(subCategories) {
    const groupContainer = this.subCategoryContentTarget.querySelector('[data-select-group]')
    if (!groupContainer) return
    
    groupContainer.querySelectorAll('[data-select-item]').forEach(item => item.remove())
    
    subCategories.forEach(subCat => {
      const item = this.createSelectItem(subCat.age_type_id, subCat.age_type_name, 'selectSubCategory')
      groupContainer.appendChild(item)
    })
    
    const valueTarget = this.subCategoryTriggerTarget.querySelector('[data-ruby-ui--select-target="value"]')
    if (valueTarget) valueTarget.innerText = 'Select sub-category'
    this.ageTypeConfigIdTarget.value = ''
    
    const subCategoryInput = this.subCategorySelectTarget.querySelector('[data-ruby-ui--select-target="input"]')
    if (subCategoryInput) subCategoryInput.value = ''
  }

  createSelectItem(value, text, action) {
    const template = this.itemTemplateTarget.content.cloneNode(true)
    const item = template.firstElementChild
    
    item.dataset.value = value
    item.dataset.action = `click->${this.identifier}#${action}`
    item.querySelector('[data-text]').textContent = text
    
    return item
  }

  clearBreedDropdown() {
    const groupContainer = this.breedContentTarget.querySelector('[data-select-group]')
    if (groupContainer) {
      groupContainer.querySelectorAll('[data-select-item]').forEach(item => item.remove())
    }
    const valueTarget = this.breedTriggerTarget.querySelector('[data-ruby-ui--select-target="value"]')
    if (valueTarget) valueTarget.innerText = 'Select breed'
    
    const inputTarget = this.breedSelectTarget.querySelector('[data-ruby-ui--select-target="input"]')
    if (inputTarget) inputTarget.value = ''
  }

  clearCategoryDropdown() {
    const groupContainer = this.categoryContentTarget.querySelector('[data-select-group]')
    if (groupContainer) {
      groupContainer.querySelectorAll('[data-select-item]').forEach(item => item.remove())
    }
    const valueTarget = this.categoryTriggerTarget.querySelector('[data-ruby-ui--select-target="value"]')
    if (valueTarget) valueTarget.innerText = 'Select category'
    this.sexTypeConfigIdTarget.value = ''
    
    const categoryInput = this.categorySelectTarget.querySelector('[data-ruby-ui--select-target="input"]')
    if (categoryInput) categoryInput.value = ''
  }

  clearSubCategoryDropdown() {
    const groupContainer = this.subCategoryContentTarget.querySelector('[data-select-group]')
    if (groupContainer) {
      groupContainer.querySelectorAll('[data-select-item]').forEach(item => item.remove())
    }
    const valueTarget = this.subCategoryTriggerTarget.querySelector('[data-ruby-ui--select-target="value"]')
    if (valueTarget) valueTarget.innerText = 'Select sub-category'
    this.ageTypeConfigIdTarget.value = ''
    
    const subCategoryInput = this.subCategorySelectTarget.querySelector('[data-ruby-ui--select-target="input"]')
    if (subCategoryInput) subCategoryInput.value = ''
  }

  closeDropdown(selectElement) {
    const selectController = selectElement.querySelector('[data-controller*="ruby-ui--select"]')
    if (!selectController) return

    const content = selectController.querySelector('[data-ruby-ui--select-target="content"]')
    const trigger = selectController.querySelector('[data-ruby-ui--select-target="trigger"]')
    
    if (content && !content.classList.contains('tw-hidden')) {
      content.classList.add('tw-hidden')
      trigger?.setAttribute('aria-expanded', 'false')
      selectController.setAttribute('data-ruby-ui--select-open-value', 'false')
    }
  }
}
